import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { finalize, forkJoin, Observable, of, switchMap } from 'rxjs';
import { CustomerCampaignService } from '../../../../services/customer-campaign.service';
import { KeywordResearchService } from '../../../../services/keyword-research.service';
import { KeywordConfigService } from '../../../../services/keyword-config.service';
import { TrackingKeywordSuggestion, KeywordConfig, KeywordResearchTypes, KeywordSuggestionSources, PageSuggestion, PageSuggestionSources, SaveActions } from '../../../../shared/models/keywords/keywords';
import { TabPanelDisplayMode } from 'app/components/enums/TabPanelDisplayMode';

@Component({
  selector: 'app-components-shared-keyword-research',
  templateUrl: './keyword-research.component.html',
  styleUrls: ['./keyword-research.component.scss']
})
export class KeywordResearchComponent implements OnInit {
  @Input() keywordResearchType: KeywordResearchTypes;
  @Input() customer: Boo.Objects.Customer;
  @Output() newPagesRequested = new EventEmitter<void>();

  tabPanelDisplayMode = TabPanelDisplayMode;
  pageSuggestions: PageSuggestion[];
  trackingKeywordSuggestions: TrackingKeywordSuggestion[];
  keywordConfigs: KeywordConfig[];
  keywordConfig: KeywordConfig;
  originalIsCampaignNational?: boolean;
  loading = false;
  submitting = false;
  inProgress = false;
  loaded = false; // true only once section data has successfully loaded; gates the editable view and save()
  errorLoading = false;
  refreshing = false;

  constructor(
    private customerCampaignService: CustomerCampaignService,
    private keywordResearchService: KeywordResearchService,
    private keywordConfigService: KeywordConfigService) { }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.errorLoading = false;
    this.keywordResearchService.keywordResearchInProgress(this.customer.CustomerId, this.getRequestSource())
      .pipe(
        switchMap(inProgress => {
          this.inProgress = inProgress;
          return inProgress ? of(null) : this.loadData();
        }),
        finalize(() => this.loading = false)
      )
      .subscribe({ error: (e) => { this.errorLoading = true; toastr.error(e); } });
  }

  loadData(): Observable<void> {
    this.originalIsCampaignNational = this.customer.IsCampaignNational;

    if (this.customer.IsCampaignNational == null) {
      this.customer.IsCampaignNational = false; // Default to local if not set
    }

    return forkJoin([
      this.keywordResearchService.getTrackingKeywordSuggestions(this.customer.CustomerId),
      this.keywordResearchService.getPageSuggestions(this.customer.CustomerId),
      this.keywordConfigService.getAll(this.customer.CustomerId)
    ]).pipe(
      switchMap(([trackingKeywordSuggestions, pageSuggestions, keywordConfigs]) => {
        this.trackingKeywordSuggestions = trackingKeywordSuggestions;
        this.pageSuggestions = pageSuggestions;
        this.keywordConfigs = keywordConfigs;
        this.getKeywordConfig();
        this.loaded = true;
        return of(null);
      })
    );
  }

  refresh(): void {
    this.refreshing = true;
    this.errorLoading = false;
    this.keywordResearchService.keywordResearchInProgress(this.customer.CustomerId, this.getRequestSource())
      .pipe(
        switchMap(inProgress => {
          this.inProgress = inProgress;

          if (inProgress) {
            toastr.info('Keyword research is still in progress.');
            return of(null);
          }

          this.loading = true;
          return this.loadData().pipe(finalize(() => this.loading = false));
        }),
        finalize(() => this.refreshing = false)
      )
      .subscribe({ error: (e) => { this.errorLoading = true; toastr.error(e); } });
  }

  getKeywordConfig(): void {
    this.keywordConfig = this.keywordConfigs.find(x => x.IsCampaignNational === this.customer.IsCampaignNational);
  }

  isValid(): boolean {
    return this.loaded && this.validationMessage === '';
  }

  save(saveActions?: SaveActions[]): Observable<void> {
    if (this.inProgress || !this.loaded) {
      return of(null); // nothing to save unless the editable section is loaded and research isn't running
    }

    return this.keywordResearchService.save(
      this.customer.CustomerId,
      this.pageSuggestions.filter(x => x.IsModified && x.IsCampaignNational === this.customer.IsCampaignNational),
      this.trackingKeywordSuggestions.filter(x => x.IsModified && x.IsCampaignNational === this.customer.IsCampaignNational),
      saveActions ?? [],
      this.getRequestSource(),
      this.isCampaignTypeModified() ? this.customer.IsCampaignNational : null
    );
  }

  generateReport(): void {
    this.save([SaveActions.GenerateSeoActionPlanDraft])
      .pipe(switchMap(() => this.loadData()))
      .subscribe();
  }

  researchRequested(): void {
    this.submitting = true;
    this.pageSuggestions.filter(x => x.IsModified && x.IsCampaignNational === this.customer.IsCampaignNational && x.PendingKeywordResearch)
      .forEach(x => {
        x.KeywordResearch = [x.PendingKeywordResearch];
        x.KeywordSuggestions = x.KeywordSuggestions.filter(y => y.SourceId === KeywordSuggestionSources.CustomerCampaign); // Only keep CustomerCampaign keywords
      });
    this.save([SaveActions.ProcessKeywordResearch]).subscribe({
      complete: () => {
        this.submitting = false;
        this.inProgress = true;
        this.keywordResearchService.getExpectedCompletionMinutes()
          .subscribe(minutes => toastr.info(`Keyword research requested. Results will be available within ${minutes} minutes.`));
      },
      error: (e) => {
        this.submitting = false;
        toastr.error(e);
      }
    });
  }

  pagesRequested(): void {
    if (this.keywordResearchType !== KeywordResearchTypes.SeoOnboarding) {
      return;
    }

    this.keywordResearchService.requestNewPageSuggestions(this.customer.CustomerId).subscribe({ complete: () => this.newPagesRequested.emit() });
  }

  get validationMessage(): string {
    switch (this.keywordResearchType) {
      case KeywordResearchTypes.SeoOnboarding:
        return this.getOnboardingValidationMessage();
      case KeywordResearchTypes.StrategyUpdate:
        return this.getStrategyUpdateValidationMessage();
      default:
        return '';
    }
  }

  private getOnboardingValidationMessage(): string {
    if (this.pageSuggestions.some(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational && x.Url && !this.customerCampaignService.domainMatches(this.customer.Url, x.Url))) {
      return 'All page domains must match the customer domain.';
    }

    const requiredPages = this.keywordConfig?.MinimumPages || 1;
    const requiredKeywordsPerPage = (this.keywordConfig?.MinimumMainKeywords || 1) / requiredPages;

    if (this.pageSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length < requiredPages) {
      return `At least ${requiredPages} pages must be selected.`;
    }

    if (this.pageSuggestions.some(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational && x.KeywordSuggestions.filter(y => y.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length < requiredKeywordsPerPage)) {
      return 'All pages must have at least ' + requiredKeywordsPerPage + ' keywords.';
    }

    const requiredTrackingKeywords = this.keywordConfig?.MinimumTrackingKeywords || 1;
    const maximumTrackingKeywords = this.keywordConfig?.MaximumTrackingKeywords || 100;

    if (this.trackingKeywordSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length < requiredTrackingKeywords) {
      return `At least ${requiredTrackingKeywords} tracking keywords must be selected.`;
    }

    if (this.trackingKeywordSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length > maximumTrackingKeywords) {
      return `No more than ${maximumTrackingKeywords} tracking keywords can be selected.`;
    }

    const keywordPhrases = this.pageSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational)
      .flatMap(x => x.KeywordSuggestions)
      .filter(x => x.IsSelected)
      .map(x => x.KeywordPhrase.toLowerCase().trim())
      .concat(this.trackingKeywordSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).map(x => x.KeywordPhrase.toLowerCase().trim()));

    if (new Set(keywordPhrases).size !== keywordPhrases.length) {
      return 'Keywords cannot be duplicated.';
    }

    return '';
  }

  private getStrategyUpdateValidationMessage(): string {
    if (this.pageSuggestions.some(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational && x.Url && !this.customerCampaignService.domainMatches(this.customer.Url, x.Url))) {
      return 'All page domains must match the customer domain.';
    }

    const requiredPages = this.keywordConfig?.MinimumPages || 1;
    const requiredKeywordsPerPage = (this.keywordConfig?.MinimumMainKeywords || 1) / requiredPages;

    if (this.pageSuggestions.some(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational && x.KeywordSuggestions.filter(y => y.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length < requiredKeywordsPerPage)) {
      return 'All pages must have at least ' + requiredKeywordsPerPage + ' keywords.';
    }

    const requiredTrackingKeywords = this.keywordConfig?.MinimumTrackingKeywords || 1;
    const maximumTrackingKeywords = this.keywordConfig?.MaximumTrackingKeywords || 100;

    if (this.trackingKeywordSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length < requiredTrackingKeywords) {
      return `At least ${requiredTrackingKeywords} tracking keywords must be selected.`;
    }

    if (this.trackingKeywordSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length > maximumTrackingKeywords) {
      return `No more than ${maximumTrackingKeywords} tracking keywords can be selected.`;
    }

    const keywordPhrases = this.pageSuggestions.filter(x => (x.SourceId === PageSuggestionSources.CustomerCampaign || x.IsSelected) && x.IsCampaignNational === this.customer.IsCampaignNational)
      .flatMap(x => x.KeywordSuggestions)
      .filter(x => x.IsSelected)
      .map(x => x.KeywordPhrase.toLowerCase().trim())
      .concat(this.trackingKeywordSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).map(x => x.KeywordPhrase.toLowerCase().trim()));

    if (new Set(keywordPhrases).size !== keywordPhrases.length) {
      return 'Keywords cannot be duplicated.';
    }

    return '';
  }

  private isCampaignTypeModified(): boolean {
    return this.originalIsCampaignNational !== undefined
      && this.customer.IsCampaignNational !== this.originalIsCampaignNational;
  }

  private getRequestSource(): Boo.Objects.Enums.RequestSources {
    return this.keywordResearchType === KeywordResearchTypes.SeoOnboarding
      ? Boo.Objects.Enums.RequestSources.SeoOnboarding
      : Boo.Objects.Enums.RequestSources.StrategyUpdate;
  }
}